import { CellClassParams, ICellRendererParams, ValueFormatterFunc, ValueFormatterParams } from 'ag-grid-community';
import { CommodityModel, Currency, StressIncrementType } from '../../../../../../Generated/Commodity-Management-Api';
import Formatting from '../../../../../../Helpers/Formatting';
import { StoneXNumber } from '../../../../../StoneXMui';

export interface ExposureReportConfiguration {
    behavior?: {
        stressIncrementType?: StressIncrementType;
        stressIncrements?: number[];
    };
    columns: {
        available: ExposureReportColumn[];
        active: ExposureReportColumn[];
    };
}

export interface ExposureReportUserOverride {
    behavior?: {
        stressIncrementType?: StressIncrementType;
        stressIncrements?: number[];
    };
    columns?: {
        active?: ExposureReportColumn[];
    };
}

export type ExposureRowType = 'Counterparty' | 'Commodity' | 'DealTicket' | 'Position';

export interface ExposureReportRow {
    path: string[];
    rowType: ExposureRowType;

    counterpartyId?: number;
    counterpartyName?: string;
    counterpartyCreditLimit?: number | null;
    availableCredit?: number | null;
    counterpartyNetting?: boolean;
    counterpartySetOff?: boolean;

    commodity?: CommodityModel;

    tradeId?: number | null;
    dealTicket?: string | null;

    positionId?: number;
    portfolioId?: number | null;
    portfolioName?: string | null;
    side?: string | null;
    contractMonth?: string | null;
    volume?: number | null;
    price?: number | null;
    marketPrice?: number | null;
    markToMarketPrice?: number | null;

    markToMarketValue?: number | null;
    stressPrices?: { [increment: string]: number | null };
    stressValues?: { [increment: string]: number | null };
    availableCredits?: { [increment: string]: number };
}

interface ExposureColumnConfiguration {
    colId: string;
    headerName?: string;
    field?: string;
    minWidth?: number;
    flex?: number;
    sortable?: boolean;
    filter?: string;
    cellClass?: string | string[] | ((params: CellClassParams<ExposureReportRow, any>) => string | string[] | undefined);
    valueFormatter?: (params: ValueFormatterParams<ExposureReportRow>) => string | ValueFormatterFunc<ExposureReportRow, any>;
    cellRenderer?: (params: ICellRendererParams<ExposureReportRow>) => any;
}

export type ExposureReportColumn =
    | 'CreditLimit'
    | 'AvailableCredit'
    | 'Portfolio'
    | 'ContractMonth'
    | 'Side'
    | 'Volume'
    | 'Price'
    | 'MarketPrice'
    | 'MarkToMarketPrice'
    | 'StressPrice'
    | 'StressValue';

export const USER_SELECTABLE_COLUMNS: ExposureReportColumn[] = [
    'CreditLimit',
    'AvailableCredit',
    'Portfolio',
    'ContractMonth',
    'Side',
    'Volume',
    'Price',
    'MarketPrice',
    'MarkToMarketPrice',
    'StressPrice',
    'StressValue',
];

export const ExposureColumns: { [key in ExposureReportColumn]: ExposureColumnConfiguration } = {
    CreditLimit: {
        colId: 'CreditLimit',
        headerName: 'Credit Limit',
        field: 'counterpartyCreditLimit',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params) => <StoneXNumber number={params.value} currency={Currency.Usd}></StoneXNumber>,
        minWidth: 120,
        flex: 1,
    },
    AvailableCredit: {
        colId: 'AvailableCredit',
        headerName: 'Available Credit',
        field: 'availableCredit',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params) => <StoneXNumber number={params.value} currency={Currency.Usd}></StoneXNumber>,
        minWidth: 130,
        flex: 1,
    },
    Portfolio: {
        colId: 'Portfolio',
        headerName: 'Portfolio',
        field: 'portfolioName',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 120,
        flex: 1,
    },
    ContractMonth: {
        colId: 'ContractMonth',
        headerName: 'Contract Month',
        field: 'contractMonth',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 110,
        flex: 1,
    },
    Side: {
        colId: 'Side',
        headerName: 'Side',
        field: 'side',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 80,
        flex: 1,
    },
    Volume: {
        colId: 'Volume',
        headerName: 'Volume',
        field: 'volume',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params) => <StoneXNumber number={params.value}></StoneXNumber>,
        minWidth: 100,
        flex: 1,
    },
    Price: {
        colId: 'Price',
        headerName: 'Price',
        field: 'price',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params) => <StoneXNumber number={params.value} currency={Currency.Usd} decimals={params.data?.commodity?.displayPrecision}></StoneXNumber>,
        minWidth: 100,
        flex: 1,
    },
    MarketPrice: {
        colId: 'MarketPrice',
        headerName: 'Market Price',
        field: 'marketPrice',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params) => <StoneXNumber number={params.value} currency={Currency.Usd} decimals={params.data?.commodity?.displayPrecision}></StoneXNumber>,
        minWidth: 110,
        flex: 1,
    },
    MarkToMarketPrice: {
        colId: 'MarkToMarketPrice',
        headerName: 'MTM Price',
        field: 'markToMarketPrice',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params) => <StoneXNumber number={params.value} currency={Currency.Usd} decimals={params.data?.commodity?.displayPrecision}></StoneXNumber>,
        minWidth: 140,
        flex: 1,
    },
    StressPrice: {
        colId: 'StressPrice',
        headerName: 'Stress Price',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params) => <StoneXNumber number={params.value} currency={Currency.Usd} decimals={params.data?.commodity?.displayPrecision}></StoneXNumber>,
        minWidth: 130,
        flex: 1,
    },
    StressValue: {
        colId: 'StressValue',
        headerName: 'MTM Exposure',
        filter: 'agNumberColumnFilter',
        cellClass: (params) => Formatting.getAgGridProfitLossCellClassesFlipped(params),
        cellRenderer: (params) => <StoneXNumber number={params.value} currency={Currency.Usd} colored flipped></StoneXNumber>,
        minWidth: 140,
        flex: 1,
    },
};
